import React from 'react';

const PrivacyPolicy: React.FC = () => {
    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
                <h1 className="text-3xl font-bold text-slate-800 mb-2">Privacy Policy for Catalog Muse</h1>
                <p className="text-slate-500 mb-8">Effective Date: November 25, 2025</p>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Data Controller</h2>
                    <p className="text-slate-600 leading-relaxed">
                        <strong>Amin Boulabaim Amjahid</strong><br />
                        NIF/DNI: ES25733918T<br />
                        Registered Address: C/ Loma de los Riscos Nº59, 29620 Málaga, Spain<br />
                        Email: amin@innerunionhome.com<br />
                        Alternative Contact: info@innerunionhome.com
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Introduction</h2>
                    <p className="text-slate-600 leading-relaxed">
                        This Privacy Policy explains how <strong>Catalog Muse</strong> ("the App"), operated by Amin Boulabaim Amjahid,
                        collects, uses, and protects personal and store data from Shopify merchants ("Merchants") and their customers.
                    </p>
                    <p className="text-slate-600 leading-relaxed mt-3">
                        We comply with the General Data Protection Regulation (EU) 2016/679 (GDPR), the Spanish Organic Law 3/2018 (LOPDGDD),
                        and applicable international laws including the California Consumer Privacy Act (CCPA/CPRA).
                    </p>
                    <p className="text-slate-600 leading-relaxed mt-3">
                        By installing and using the App, you agree to the terms described below.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">3. Information We Collect</h2>

                    <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">A. Information from Merchants</h3>
                    <p className="text-slate-600 leading-relaxed">When you install Catalog Muse, we collect the following information from your Shopify account:</p>
                    <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                        <li><strong>Contact Information:</strong> Name, email address, and phone number.</li>
                        <li><strong>Store Information:</strong> Shop domain (.myshopify.com), shop ID, and primary locale.</li>
                        <li><strong>Technical Data:</strong> IP address, browser type, and device information used to access the App.</li>
                    </ul>

                    <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">B. Store Data (Product Information)</h3>
                    <p className="text-slate-600 leading-relaxed">To provide our AI content generation services, the App accesses and processes:</p>
                    <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                        <li><strong>Product Data:</strong> Titles, descriptions, prices, variants, and images of products in your store.</li>
                        <li><strong>Content Data:</strong> Blog posts, pages, and other content you choose to generate or edit using the App.</li>
                    </ul>

                    <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">C. User-Provided Credentials</h3>
                    <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
                        <li><strong>Pinecone Credentials:</strong> The App requires you to provide your own Pinecone API Key, Environment, and Index Name. These credentials are used solely to connect your store to your Pinecone vector database for product recommendations.</li>
                    </ul>

                    <h3 className="text-lg font-medium text-slate-700 mt-4 mb-2">D. Information from Your Customers</h3>
                    <p className="text-slate-600 leading-relaxed">
                        The App generally does not collect personal data directly from your store's customers (shoppers). However, if you use features that analyze order history for trends (future features), we may process anonymized or aggregated order data.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Purpose and Legal Basis of Processing</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">We process your data for the following purposes:</p>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm text-slate-600 border border-slate-200 rounded-lg">
                            <thead className="bg-slate-100">
                                <tr>
                                    <th className="text-left p-3 font-semibold">Purpose</th>
                                    <th className="text-left p-3 font-semibold">Legal Basis</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr className="border-t border-slate-200">
                                    <td className="p-3">Service Provision: To generate blog posts, Pinterest pins, and product images using AI.</td>
                                    <td className="p-3">Contractual Necessity</td>
                                </tr>
                                <tr className="border-t border-slate-200 bg-slate-50">
                                    <td className="p-3">Product Indexing: To upload and sync your product catalog with your Pinecone vector database.</td>
                                    <td className="p-3">Contractual Necessity</td>
                                </tr>
                                <tr className="border-t border-slate-200">
                                    <td className="p-3">App Administration: To manage your account, billing (via Shopify), and provide customer support.</td>
                                    <td className="p-3">Legitimate Interest</td>
                                </tr>
                                <tr className="border-t border-slate-200 bg-slate-50">
                                    <td className="p-3">Improvement: To analyze App usage and improve our AI algorithms and user interface.</td>
                                    <td className="p-3">Legitimate Interest</td>
                                </tr>
                                <tr className="border-t border-slate-200">
                                    <td className="p-3">Compliance: To comply with tax, legal, and Shopify App Store requirements.</td>
                                    <td className="p-3">Legal Obligation</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Data Sharing and Third Parties</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">We do not sell your data. We share data only with the service providers necessary to operate the App:</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                        <li><strong>Shopify:</strong> The platform hosting your store and handling billing/authentication.</li>
                        <li><strong>Render:</strong> Our cloud hosting provider (servers located in the US/EU).</li>
                        <li><strong>Pinecone:</strong> Your product data is sent to your specific Pinecone index using the credentials you provide. We do not have access to your Pinecone data outside of the App's intended functionality.</li>
                        <li><strong>Google Gemini (AI):</strong> We send product titles, descriptions, and prompts to Google's Generative AI API to generate text and image content.</li>
                        <li><strong>Analytics Tools:</strong> We may use tools like Microsoft Clarity or Google Analytics to monitor App performance.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">6. International Data Transfers</h2>
                    <p className="text-slate-600 leading-relaxed">
                        Because our underlying infrastructure (Render, Pinecone, Google AI) operates globally, your information may be processed outside the European Economic Area (EEA). We ensure such transfers comply with GDPR using Standard Contractual Clauses (SCCs) or equivalent safeguards.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Data Retention</h2>
                    <ul className="list-disc list-inside text-slate-600 space-y-2">
                        <li><strong>App Data:</strong> We retain your account data for as long as the App is installed on your store.</li>
                        <li><strong>Generated Content:</strong> Content you save to Shopify is retained by Shopify according to their policies.</li>
                        <li><strong>Pinecone Data:</strong> Data uploaded to Pinecone is managed by you in your Pinecone account. You are responsible for managing retention within Pinecone.</li>
                        <li><strong>Logs:</strong> Server logs are retained for 30 days for security and debugging.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">8. Your Rights</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">Under GDPR and CCPA, you have the right to:</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                        <li>Access the personal data we hold about you.</li>
                        <li>Request correction or deletion of your data.</li>
                        <li>Withdraw consent (e.g., by uninstalling the App).</li>
                        <li>Request data portability.</li>
                    </ul>
                    <p className="text-slate-600 leading-relaxed mt-3">
                        To exercise these rights, please contact us at <strong>amin@innerunionhome.com</strong>.
                    </p>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">9. Data Security</h2>
                    <p className="text-slate-600 leading-relaxed mb-3">We protect your information with:</p>
                    <ul className="list-disc list-inside text-slate-600 space-y-1">
                        <li><strong>Encryption:</strong> All data is transmitted via HTTPS (SSL/TLS).</li>
                        <li><strong>Secure Storage:</strong> API keys and sensitive tokens are encrypted at rest.</li>
                        <li><strong>Access Control:</strong> Strict access controls for our development team.</li>
                    </ul>
                </section>

                <section className="mb-8">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">10. Changes to This Policy</h2>
                    <p className="text-slate-600 leading-relaxed">
                        We may update this Privacy Policy. Changes will be notified via the App dashboard or email.
                    </p>
                </section>

                <section className="border-t border-slate-200 pt-6">
                    <h2 className="text-xl font-semibold text-slate-800 mb-3">11. Contact Information</h2>
                    <p className="text-slate-600 leading-relaxed">
                        <strong>Amin Boulabaim Amjahid</strong><br />
                        C/ Loma de los Riscos Nº59, 29620 Málaga, Spain<br />
                        📧 amin@innerunionhome.com
                    </p>
                </section>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
